import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { AccountInitialisationError, initialiseAccount } from '../utils/accountInitialisation';

const AuthContext = createContext();
const userPool = new CognitoUserPool({ UserPoolId: 'eu-west-2_nyRkWiHVC', ClientId: 'fsp09nn4h6a084bod215if65q' });

const sessionFor = (user, refresh = false) => new Promise((resolve, reject) => {
  if (!user) { reject(new Error('No signed-in session')); return; }
  user.getSession((error, session) => {
    if (error || !session?.isValid()) { reject(error || new Error('Session is not valid')); return; }
    if (refresh) user.refreshSession(session.getRefreshToken(), (refreshError, renewed) => refreshError ? reject(refreshError) : resolve(renewed));
    else resolve(session);
  });
});
const untilAborted = (promise, signal) => new Promise((resolve, reject) => {
  const abort = () => reject(new DOMException('Account loading interrupted', 'AbortError'));
  if (signal.aborted) { abort(); return; }
  signal.addEventListener('abort', abort, { once: true });
  promise.then(value => { signal.removeEventListener('abort', abort); resolve(value); },
    error => { signal.removeEventListener('abort', abort); reject(error); });
});
const attributesFor = user => new Promise((resolve, reject) => user.getUserAttributes((error, attributes) => {
  if (error) reject(error);
  else resolve(Object.fromEntries(attributes.map(attribute => [attribute.getName(), attribute.getValue()])));
}));

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [userAttributes, setUserAttributes] = useState({});
  const [userProfileSlug, setUserProfileSlug] = useState('');
  const [accountReady, setAccountReady] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState(null);
  const inFlight = useRef(null);
  const currentUsername = useRef(null);

  const updateUserProfileSlug = useCallback(slug => {
    localStorage.setItem('userProfileSlug', slug);
    setUserProfileSlug(slug);
  }, []);

  const resetAccount = useCallback(() => {
    inFlight.current?.controller.abort();
    inFlight.current = null;
    currentUsername.current = null;
    localStorage.removeItem('userSub');
    localStorage.removeItem('userProfileSlug');
    setUserAttributes({}); setUserProfileSlug('');
    setAccountReady(false); setAccountLoading(false); setAccountError(null);
    setIsAuthenticated(false);
  }, []);

  const fetchUserAttributes = useCallback((options = {}) => {
    const user = userPool.getCurrentUser();
    if (!user) return Promise.reject(new Error('No signed-in session'));
    const username = user.getUsername();
    if (inFlight.current?.username === username && !options.refreshSession) return inFlight.current.promise;
    inFlight.current?.controller.abort();
    const request = { username, controller: new AbortController() };
    inFlight.current = request;
    const current = () => inFlight.current === request && userPool.getCurrentUser()?.getUsername() === username;
    if (currentUsername.current !== username) {
      setUserAttributes({}); setUserProfileSlug('');
      localStorage.removeItem('userSub'); localStorage.removeItem('userProfileSlug');
    }
    currentUsername.current = username;
    setAccountLoading(true); setAccountReady(false); setAccountError(null);
    request.promise = (async () => {
      let attrs;
      let authenticated = false;
      const timeout = setTimeout(() => request.controller.abort(), 18000);
      try {
        let session = await untilAborted(sessionFor(user, options.refreshSession), request.controller.signal);
        authenticated = true;
        attrs = await untilAborted(attributesFor(user), request.controller.signal);
        if (!current()) throw new Error('Session changed');
        setIsAuthenticated(true);
        setUserAttributes(attrs);
        let account;
        try { account = await initialiseAccount(session.getIdToken().getJwtToken(), request.controller.signal); }
        catch (error) {
          // Attribute verification can happen in another tab while this valid
          // ID token still contains the old flag. Refresh once before asking
          // someone whose email is now verified to repeat that step.
          if (error.code !== 'email_verification_required' || !['true', true].includes(attrs.email_verified)) throw error;
          session = await untilAborted(sessionFor(user, true), request.controller.signal);
          account = await initialiseAccount(session.getIdToken().getJwtToken(), request.controller.signal);
        }
        if (!current()) throw new Error('Session changed');
        if (account.sub !== attrs.sub) throw new Error('Account identity mismatch');
        const loaded = { ...attrs, 'custom:userRole': account.user_role };
        setUserAttributes(loaded);
        localStorage.setItem('userSub', account.sub);
        updateUserProfileSlug(account.url_slug);
        setAccountReady(true);
        return loaded;
      } catch (error) {
        const failure = error instanceof AccountInitialisationError ? error
          : new AccountInitialisationError('account_initialisation_failed', 'We couldn’t finish loading your ViewFinders account. Please try again.');
        failure.email = attrs?.email;
        failure.sub = attrs?.sub;
        if (current()) {
          setIsAuthenticated(authenticated);
          setAccountError(failure);
        }
        throw failure;
      } finally {
        clearTimeout(timeout);
        if (current()) { inFlight.current = null; setAccountLoading(false); }
      }
    })();
    return request.promise;
  }, [updateUserProfileSlug]);

  useEffect(() => {
    let active = true;
    const restore = async () => {
      if (!userPool.getCurrentUser()) resetAccount();
      else {
        try { await fetchUserAttributes(); } catch { /* Recovery is shown by protected routes. */ }
      }
      if (active) setAuthInitialized(true);
    };
    restore();
    const changed = event => {
      if (event.key === null || event.key?.startsWith('CognitoIdentityServiceProvider.')) restore();
    };
    window.addEventListener('storage', changed);
    return () => { active = false; window.removeEventListener('storage', changed); inFlight.current?.controller.abort(); inFlight.current = null; };
  }, [fetchUserAttributes, resetAccount]);

  const getAccessToken = useCallback(async () => (await sessionFor(userPool.getCurrentUser())).getAccessToken().getJwtToken(), []);
  const getIdToken = useCallback(async () => (await sessionFor(userPool.getCurrentUser())).getIdToken().getJwtToken(), []);
  const authFetch = useCallback(async (url, options = {}) => {
    const headers = { ...options.headers, Authorization: `Bearer ${await getIdToken()}` };
    const result = await fetch(url, { ...options, headers });
    if (result.status !== 401) return result;
    return fetch(url, { ...options, headers: { ...headers, Authorization: `Bearer ${await getIdToken()}` } });
  }, [getIdToken]);

  const requestEmailVerification = useCallback(async () => {
    const user = userPool.getCurrentUser();
    await sessionFor(user);
    return new Promise((resolve, reject) => user.getAttributeVerificationCode('email', { onSuccess: resolve, onFailure: reject }));
  }, []);
  const verifyEmailAttribute = useCallback(async code => {
    const user = userPool.getCurrentUser();
    await sessionFor(user);
    return new Promise((resolve, reject) => user.verifyAttribute('email', code, { onSuccess: resolve, onFailure: reject }));
  }, []);

  const logout = useCallback(() => {
    userPool.getCurrentUser()?.signOut();
    resetAccount();
    localStorage.removeItem('authToken'); localStorage.removeItem('openChats');
    setAuthInitialized(true);
  }, [resetAccount]);

  return <AuthContext.Provider value={{
    isAuthenticated, authInitialized, setIsAuthenticated, userAttributes,
    fetchUserAttributes, userProfileSlug, updateUserProfileSlug, accountReady,
    accountLoading, accountError, getAccessToken, getIdToken, authFetch, logout,
    requestEmailVerification, verifyEmailAttribute,
  }}>{children}</AuthContext.Provider>;
};
export const useAuth = () => useContext(AuthContext);
